// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2)
// version  : 1.1
// updated  : 2026-08-04
// protected: NO  -> Fase 6C: komponen kamera REUSABLE (camera-only) untuk Kasir.
//            Hanya emit kode hasil scan via onScan(code); TIDAK memvalidasi bisnis.
//            Validasi item tetap di logic existing pemanggil (Kasir -> /api/v2/sales).
//            Tidak menyentuh backend/ledger/QR generator/formula.
//            v1.1: perf kamera adaptif (BarcodeDetector native + resolusi ideal 720p +
//            formatsToSupport QR_CODE) — mempercepat iOS tanpa menurunkan Android.
// =============================================================================
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Zap, ZapOff, AlertTriangle, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { parseQr } from "@/v2/components/QrBatchInput";

const beep = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.12, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.15);
    o.start(); o.stop(ctx.currentTime + 0.16);
    o.onended = () => { try { ctx.close(); } catch (_) {} };
  } catch (_) { /* noop */ }
};
const vibrate = (ms = 60) => { try { if (navigator.vibrate) navigator.vibrate(ms); } catch (_) {} };

/**
 * QrCameraButton — tombol "Scan via Kamera" + dialog scanner kamera HP.
 * Props:
 *  - onScan(code): dipanggil tiap QR ter-decode (kode sudah di-normalize via parseQr).
 *  - label, testid, variant.
 * Anti-dobel frame: kode sama dalam 1.5s diabaikan (QR masih di depan kamera).
 * Dedup daftar akhir = tanggung jawab pemanggil (addQr existing).
 */
export const QrCameraButton = ({ onScan, label = "Scan via Kamera", testid = "qr-cam", variant = "secondary" }) => {
  const [camOpen, setCamOpen] = useState(false);
  const [camErr, setCamErr] = useState("");
  const [starting, setStarting] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);

  const qrRef = useRef(null);
  const onScanRef = useRef(onScan);
  const lastRef = useRef({ code: "", t: 0 });
  const regionId = useRef(`qr-cam-${Math.random().toString(36).slice(2, 9)}`).current;
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  const handleDecoded = useCallback((decodedText) => {
    const now = Date.now();
    const code = parseQr(decodedText);
    if (!code) return;
    if (lastRef.current.code === code && now - lastRef.current.t < 1500) return;
    lastRef.current = { code, t: now };
    setSessionCount((c) => c + 1);
    beep(); vibrate();
    try { onScanRef.current && onScanRef.current(code); } catch (_) {}
  }, []);

  const stopCamera = useCallback(async () => {
    const inst = qrRef.current;
    qrRef.current = null;
    if (inst) {
      try { await inst.stop(); } catch (_) {}
      try { await inst.clear(); } catch (_) {}
    }
  }, []);

  const startCamera = useCallback(async () => {
    await stopCamera();
    setCamErr(""); setStarting(true); setTorchOn(false); setTorchSupported(false);
    try {
      // Konstruktor ADAPTIF:
      //  - formatsToSupport QR_CODE saja -> scanner tak buang tenaga mencoba format lain.
      //  - useBarCodeDetectorIfSupported -> pakai BarcodeDetector BAWAAN perangkat bila ada
      //    (Safari/iOS & Chrome/Android). Bila tak didukung (mis. Firefox lama), html5-qrcode
      //    OTOMATIS fallback ke decoder JS lama -> Android yang sudah baik minimal tetap sama.
      const inst = new Html5Qrcode(regionId, {
        verbose: false,
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      });
      qrRef.current = inst;
      const config = {
        fps: 10,
        qrbox: (vw, vh) => { const m = Math.floor(Math.min(vw, vh) * 0.7); return { width: m, height: m }; },
        aspectRatio: 1.0,
        // Batasi resolusi frame supaya iPhone tak memproses frame beresolusi sangat tinggi.
        // WAJIB 'ideal' (BUKAN 'exact') -> kamera tetap terbuka walau resolusi tak persis didukung.
        // 720p cukup untuk QR 22mm (label thermal 50x40mm) & jauh lebih ringan dari default iOS.
        videoConstraints: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };
      // Bila videoConstraints diberikan, ia menggantikan argumen pertama; facingMode tetap
      // disertakan di dalamnya (ideal) agar kamera belakang tetap terpilih.
      await inst.start({ facingMode: "environment" }, config, handleDecoded, () => {});
      try {
        const caps = inst.getRunningTrackCapabilities();
        if (caps && caps.torch) setTorchSupported(true);
      } catch (_) { /* torch tak didukung */ }
    } catch (e) {
      const msg = String((e && (e.message || e.name)) || "");
      if (/permission|denied|NotAllowed/i.test(msg))
        setCamErr("Akses kamera ditolak. Izinkan kamera di pengaturan browser, lalu coba lagi.");
      else if (/NotFound|no camera|Requested device|NotReadable|in use/i.test(msg))
        setCamErr("Kamera tidak ditemukan / sedang dipakai aplikasi lain.");
      else
        setCamErr("Tidak bisa membuka kamera. Pastikan perangkat punya kamera & izin diberikan.");
      await stopCamera();
    } finally { setStarting(false); }
  }, [handleDecoded, stopCamera, regionId]);

  useEffect(() => {
    if (camOpen) {
      setSessionCount(0);
      const t = setTimeout(() => { startCamera(); }, 180);
      return () => clearTimeout(t);
    }
    stopCamera();
    return undefined;
  }, [camOpen, startCamera, stopCamera]);

  useEffect(() => () => { stopCamera(); }, [stopCamera]);

  const toggleTorch = async () => {
    const inst = qrRef.current;
    if (!inst) return;
    const next = !torchOn;
    try {
      await inst.applyVideoConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (_) {
      setTorchSupported(false);
      toast.error("Senter tidak didukung perangkat ini.");
    }
  };

  const retry = () => { setCamErr(""); setTimeout(() => { startCamera(); }, 150); };

  return (
    <>
      <Button type="button" variant={variant} className="shrink-0" onClick={() => setCamOpen(true)} data-testid={`${testid}-open`}>
        <Camera className="w-4 h-4 sm:mr-1.5" />
        <span className="hidden sm:inline">{label}</span>
      </Button>

      <Dialog open={camOpen} onOpenChange={setCamOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden gap-0" data-testid={`${testid}-dialog`}>
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Camera className="w-5 h-5" />Scan QR via Kamera
            </DialogTitle>
          </DialogHeader>
          <div className="px-4 pb-4 space-y-3">
            {camErr ? (
              <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive flex gap-2" data-testid={`${testid}-error`}>
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <p>{camErr}</p>
                  <Button size="sm" variant="outline" onClick={retry} data-testid={`${testid}-retry`}>Coba Lagi</Button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <div id={regionId} className="w-full rounded-xl overflow-hidden bg-black aspect-square" />
                {starting && (
                  <div className="absolute inset-0 flex items-center justify-center text-white/80 gap-2 text-sm">
                    <Loader2 className="w-5 h-5 animate-spin" />Memulai kamera…
                  </div>
                )}
              </div>
            )}

            {!camErr && (
              <p className="text-xs text-muted-foreground text-center">
                Arahkan kamera ke QR item. Hasil scan otomatis masuk daftar kasir.
              </p>
            )}

            <div className="flex items-center justify-between gap-2">
              <Badge variant="secondary" data-testid={`${testid}-count`}>{sessionCount} discan</Badge>
              <div className="flex items-center gap-2">
                {torchSupported && (
                  <Button type="button" size="sm" variant={torchOn ? "default" : "outline"} onClick={toggleTorch} data-testid={`${testid}-torch`}>
                    {torchOn ? <Zap className="w-4 h-4 sm:mr-1" /> : <ZapOff className="w-4 h-4 sm:mr-1" />}
                    <span className="hidden sm:inline">Senter</span>
                  </Button>
                )}
                <Button type="button" size="sm" variant="destructive" onClick={() => setCamOpen(false)} data-testid={`${testid}-close`}>
                  <X className="w-4 h-4 mr-1" />Tutup Kamera
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
